import { MessageCircle, Phone, Mail, MapPin, Clock } from "lucide-react";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/Seo";
import { EnquirySection } from "@/components/home/EnquirySection";
import { BUSINESS, WHATSAPP_URL } from "@/lib/constants";
import { track, EVENTS } from "@/lib/analytics";
import { breadcrumbSchema, localBusinessSchema } from "@/lib/schema";
import { Reveal } from "@/components/Reveal";

export default function ContactPage() {
  return (
    <>
      <Seo
        title="Contact & Wholesale Enquiry | Nandanaaya Spices Hyderabad"
        description="Contact Nandanaaya Spices in Hyderabad for wholesale cardamom, black pepper and whole spices. Send your grade and quantity requirement for a current quotation."
        path="/contact"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [localBusinessSchema(), breadcrumbSchema([{ name: "Contact", url: "/contact" }])],
        }}
      />
      <PageShell
        testid="contact-page"
        title="Contact & Wholesale Enquiry"
        subtitle="Share your product, grade and quantity requirement. We confirm current lot availability and send a written quotation."
        breadcrumbs={[{ label: "Contact" }]}
      >
        <section className="py-12 px-4 sm:px-6">
          <div className="max-w-5xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Reveal>
              <a
                href={`tel:${BUSINESS.phoneRaw}`}
                onClick={() => track(EVENTS.phoneClick, { source: "contact-page" })}
                data-testid="contact-card-phone"
                className="block h-full bg-white border border-spice-line rounded-lg p-5 space-y-2 hover:border-spice-green/50 transition-colors"
              >
                <Phone className="w-5 h-5 text-spice-green" aria-hidden="true" />
                <div className="text-xs text-spice-subtle">Call the proprietor</div>
                <div className="text-sm font-bold text-spice-ink">{BUSINESS.phoneDisplay}</div>
              </a>
            </Reveal>
            <Reveal delay={70}>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Enquire on WhatsApp (opens WhatsApp in a new tab)"
                onClick={() => track(EVENTS.whatsappClick, { source: "contact-page" })}
                data-testid="contact-card-whatsapp"
                className="block h-full bg-white border border-spice-line rounded-lg p-5 space-y-2 hover:border-spice-green/50 transition-colors"
              >
                <MessageCircle className="w-5 h-5 text-spice-whatsapp" aria-hidden="true" />
                <div className="text-xs text-spice-subtle">WhatsApp enquiry</div>
                <div className="text-sm font-bold text-spice-ink">Send prefilled message</div>
              </a>
            </Reveal>
            <Reveal delay={140}>
              <a
                href={`mailto:${BUSINESS.email}`}
                onClick={() => track(EVENTS.emailClick, { source: "contact-page" })}
                data-testid="contact-card-email"
                className="block h-full bg-white border border-spice-line rounded-lg p-5 space-y-2 hover:border-spice-green/50 transition-colors"
              >
                <Mail className="w-5 h-5 text-spice-green" aria-hidden="true" />
                <div className="text-xs text-spice-subtle">Business email</div>
                <div className="text-sm font-bold text-spice-ink break-all">{BUSINESS.email}</div>
              </a>
            </Reveal>
            <Reveal delay={210}>
              <div className="h-full bg-white border border-spice-line rounded-lg p-5 space-y-2" data-testid="contact-card-address">
                <MapPin className="w-5 h-5 text-spice-green" aria-hidden="true" />
                <div className="text-xs text-spice-subtle">Location</div>
                <div className="text-sm font-bold text-spice-ink">{BUSINESS.address}</div>
                <div className="flex items-center gap-1.5 text-[11px] text-spice-muted pt-1">
                  <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                  {BUSINESS.hours}
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <EnquirySection compactHeading />
      </PageShell>
    </>
  );
}
